import { get, post, put, patch, del, downloadBlob, uploadFile } from './client.js';
import type {
  Email,
  EmailAccount,
  EmailAttachment,
  EmailTag,
  EmailSignature,
  EmailTemplate,
  EmailTask,
  EmailTaskDetail,
  ThreadSummary,
  ActiveSignature,
  SendEmailRequest,
  SendEmailResponse,
  PaginatedResponse,
  EmailAccountListResponse,
  EmailTemplateListResponse,
  EmailTaskListResponse,
  EmailSendRecordListResponse,
  CreateEmailAccountRequest,
  UpdateEmailAccountRequest,
  CreateEmailSignatureRequest,
  UpdateEmailSignatureRequest,
  CreateEmailTemplateRequest,
  UpdateEmailTemplateRequest,
  CreateEmailTaskRequest,
  EmailTemplateCategory,
  Permission,
  PermissionListResponse,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  AssignPermissionsRequest,
  RolePermissionsResponse,
  UserPermissionsResponse,
  TaskStatus,
  FetchEmailsRequest,
} from '../types/index.js';

// ==================== 邮件 API ====================

export const emailApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    customer_id?: number;
    account_id?: number;
    is_read?: number;
    folder?: string;
    tag_id?: number;
    send_status?: number;
    is_starred?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.customer_id) query.append('customer_id', params.customer_id.toString());
    if (params?.account_id) query.append('account_id', params.account_id.toString());
    if (params?.is_read !== undefined) query.append('is_read', params.is_read.toString());
    if (params?.folder) query.append('folder', params.folder);
    if (params?.tag_id !== undefined) query.append('tag_id', params.tag_id.toString());
    if (params?.send_status !== undefined) query.append('send_status', params.send_status.toString());
    if (params?.is_starred !== undefined) query.append('is_starred', params.is_starred.toString());
    return get<PaginatedResponse<Email>>(`/emails?${query.toString()}`);
  },

  get: (id: number) => get<Email>(`/emails/${id}`),

  delete: (id: number) => del<{ message: string }>(`/emails/${id}`),

  restore: (id: number) => post<{ message: string }>(`/emails/${id}/restore`),

  getFolderCounts: () =>
    get<Array<{ folder: string; total: number; unread: number }>>('/emails/folders'),

  markAsRead: (id: number) => patch<Email>(`/emails/${id}/read`),

  markMultipleAsRead: (ids: number[]) =>
    patch<{ message: string }>('/emails/read-batch', { ids }),

  toggleStar: (id: number) =>
    patch<{ is_starred: number }>(`/emails/${id}/star`),

  getAttachments: (emailId: number) =>
    get<{ data: EmailAttachment[]; total: number }>(`/emails/${emailId}/attachments`),

  downloadAttachment: (emailId: number, attachmentId: number) =>
    downloadBlob(`/emails/${emailId}/attachments/${attachmentId}/download`),

  setTags: (emailId: number, tagIds: number[]) =>
    put(`/emails/${emailId}/tags`, { tag_ids: tagIds }),
};

// ==================== 邮件标签 API ====================

export const emailTagApi = {
  list: () => get<EmailTag[]>('/email-tags'),
  create: (data: { name: string; color: string }) => post<EmailTag>('/email-tags', data),
  update: (id: number, data: { name?: string; color?: string }) => put<EmailTag>(`/email-tags/${id}`, data),
  delete: (id: number) => del(`/email-tags/${id}`),
};

// ==================== 邮件黑名单 API ====================

export const emailBlacklistApi = {
  add: (emailAddress: string) => post<{ id: number }>('/email-blacklist', { email_address: emailAddress }),
  list: () => get<Array<{ id: number; email_address: string }>>('/email-blacklist'),
  remove: (id: number) => del(`/email-blacklist/${id}`),
  apply: () => post<{ message: string }>('/email-blacklist/apply', {}),
};

// ==================== 邮箱账户 API ====================

export const emailAccountApi = {
  list: (params?: { page?: number; per_page?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    const qs = query.toString();
    return get<EmailAccountListResponse>(`/emails/accounts${qs ? '?' + qs : ''}`);
  },

  get: (id: number) => get<EmailAccount>(`/emails/accounts/${id}`),

  create: (data: CreateEmailAccountRequest) =>
    post<EmailAccount>('/emails/accounts', data),

  update: (id: number, data: UpdateEmailAccountRequest) =>
    put<EmailAccount>(`/emails/accounts/${id}`, data),

  delete: (id: number) =>
    del<{ message: string }>(`/emails/accounts/${id}`),

  toggleSync: (id: number, sync_enabled: number) =>
    patch<{ message: string; sync_enabled: number }>(`/emails/accounts/${id}/sync`, { sync_enabled }),

  toggleStatus: (id: number, status: number) =>
    patch<{ message: string; status: number }>(`/emails/accounts/${id}/status`, { status }),

  testConnection: (id: number) =>
    post<{ success: boolean; message: string }>(`/emails/accounts/${id}/test`),

  testCredentials: (data: { address: string; password: string; imap: string }) =>
    post<{ success: boolean; message: string }>('/emails/accounts/test', data),
};

// ==================== 邮件签名 API ====================

export const signatureApi = {
  list: (accountId: number) =>
    get<EmailSignature[]>(`/emails/accounts/${accountId}/signatures`),

  create: (accountId: number, data: CreateEmailSignatureRequest) =>
    post<EmailSignature>(`/emails/accounts/${accountId}/signatures`, data),

  update: (accountId: number, signatureId: number, data: UpdateEmailSignatureRequest) =>
    put<EmailSignature>(`/emails/accounts/${accountId}/signatures/${signatureId}`, data),

  delete: (accountId: number, signatureId: number) =>
    del<{ message: string }>(`/emails/accounts/${accountId}/signatures/${signatureId}`),

  activate: (accountId: number, signatureId: number) =>
    put<{ message: string; id: number }>(`/emails/accounts/${accountId}/signatures/${signatureId}/activate`),

  uploadImage: (accountId: number, signatureId: number, file: File): Promise<{ cid: string; original_name: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    return uploadFile(`/emails/accounts/${accountId}/signatures/${signatureId}/images`, fd);
  },

  getActive: (accountId: number) =>
    get<ActiveSignature>(`/emails/accounts/${accountId}/active-signature`),
};

// ==================== 邮件发送 API ====================

export const emailSendApi = {
  send: (data: SendEmailRequest) =>
    post<SendEmailResponse>('/emails/send', data),

  saveDraft: (data: SendEmailRequest) =>
    post<{ draft_id: number }>('/emails/draft', data),

  retry: (emailId: number) =>
    post<SendEmailResponse>(`/emails/${emailId}/retry`),

  archive: (emailId: number) =>
    post<{ message: string }>(`/emails/${emailId}/archive`),
};

// ==================== 邮件线程 API ====================

export const emailThreadApi = {
  list: (params?: { page?: number; per_page?: number; account_id?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.account_id) query.append('account_id', params.account_id.toString());
    const qs = query.toString();
    return get<PaginatedResponse<ThreadSummary>>(`/emails/threads${qs ? '?' + qs : ''}`);
  },

  getThread: (threadId: string) =>
    get<{ thread_id: string; subject: string; messages: Email[] }>(
      `/emails/threads/${encodeURIComponent(threadId)}`),

  getEmailThread: (emailId: number) =>
    get<{ thread_id: string; subject: string; messages: Email[] }>(
      `/emails/${emailId}/thread`),
};

// ==================== 任务 API ====================

export const taskApi = {
  fetchEmails: (data?: FetchEmailsRequest) =>
    post<{ task_id: string; message: string; status: string }>(
      '/tasks/email/fetch',
      data
    ),

  getStatus: (taskId: string) =>
    get<TaskStatus>(`/tasks/email/status/${taskId}`),

  cleanup: (days?: number) =>
    post<{ task_id: string; message: string; status: string }>(
      '/tasks/email/cleanup',
      { days }
    ),
};

// ==================== 权限管理 API ====================

export const permissionApi = {
  list: (params?: { module?: string; is_active?: number; page?: number; per_page?: number }) => {
    const query = new URLSearchParams();
    if (params?.module) query.append('module', params.module);
    if (params?.is_active !== undefined) query.append('is_active', params.is_active.toString());
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    return get<PermissionListResponse>(`/permissions?${query.toString()}`);
  },

  modules: () => get<string[]>('/permissions/modules'),

  get: (id: number) => get<Permission>(`/permissions/${id}`),

  create: (data: CreatePermissionRequest) =>
    post<Permission>('/permissions', data),

  update: (id: number, data: UpdatePermissionRequest) =>
    put<Permission>(`/permissions/${id}`, data),

  delete: (id: number) =>
    del<{ message: string }>(`/permissions/${id}`),

  getRolePermissions: (role: string) =>
    get<RolePermissionsResponse>(`/permissions/roles/${role}/permissions`),

  getAllRolePermissions: () =>
    get<Record<string, number[]>>('/permissions/roles/all/assignments'),

  assignPermissions: (role: string, data: AssignPermissionsRequest) =>
    post<{ message: string }>(`/permissions/roles/assign`, { ...data, role }),

  getMyPermissions: () =>
    get<UserPermissionsResponse>('/permissions/me/permissions'),
};

// ==================== 邮件模板 API ====================

export const emailTemplateApi = {
  list: (params?: { page?: number; per_page?: number; status?: number; keyword?: string; category_id?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.status !== undefined) query.append('status', params.status.toString());
    if (params?.keyword) query.append('keyword', params.keyword);
    if (params?.category_id !== undefined) query.append('category_id', params.category_id.toString());
    const qs = query.toString();
    return get<EmailTemplateListResponse>(`/email-templates${qs ? '?' + qs : ''}`);
  },

  get: (id: number) => get<EmailTemplate>(`/email-templates/${id}`),

  create: (data: CreateEmailTemplateRequest) =>
    post<EmailTemplate>('/email-templates', data),

  update: (id: number, data: UpdateEmailTemplateRequest) =>
    put<EmailTemplate>(`/email-templates/${id}`, data),

  delete: (id: number) => del<{ message: string }>(`/email-templates/${id}`),

  deleteAttachment: (id: number) => del<{ message: string }>(`/email-templates/attachments/${id}`),

  deleteInlineImage: (id: number) => del<{ message: string }>(`/email-templates/inline-images/${id}`),
};

// ==================== 邮件模板分类 API ====================

export const emailTemplateCategoryApi = {
  list: () => get<{ data: EmailTemplateCategory[] }>('/email-template-categories'),
  create: (name: string) => post<EmailTemplateCategory>('/email-template-categories', { name }),
  update: (id: number, name: string) => put<EmailTemplateCategory>(`/email-template-categories/${id}`, { name }),
  delete: (id: number) => del<{ message: string }>(`/email-template-categories/${id}`),
};

// ==================== 邮件模板上传 ====================

export function uploadTemplateAttachment(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ data: { id: number; filename: string; file_size: number } }> {
  const fd = new FormData();
  fd.append('file', file);
  return uploadFile('/email-templates/attachments/upload', fd, onProgress);
}

export function uploadTemplateInlineImage(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ data: { id: number; cid: string; filename: string; file_size: number } }> {
  const fd = new FormData();
  fd.append('file', file);
  return uploadFile('/email-templates/inline-images/upload', fd, onProgress);
}

// ==================== 邮件任务 API ====================

export const emailTaskApi = {
  list: (params?: { page?: number; per_page?: number; status?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.status !== undefined) query.append('status', params.status.toString());
    const qs = query.toString();
    return get<EmailTaskListResponse>(`/email-tasks${qs ? '?' + qs : ''}`);
  },

  get: (id: number, includeDetails: boolean = true) =>
    get<EmailTaskDetail>(`/email-tasks/${id}?include_details=${includeDetails}`),

  create: (data: CreateEmailTaskRequest) =>
    post<EmailTask>('/email-tasks', data),

  delete: (id: number) => del<{ message: string }>(`/email-tasks/${id}`),

  start: (id: number) => patch<EmailTask>(`/email-tasks/${id}/start`),

  pause: (id: number) => patch<EmailTask>(`/email-tasks/${id}/pause`),

  stop: (id: number) => patch<EmailTask>(`/email-tasks/${id}/stop`),

  getSendRecords: (id: number, params?: { page?: number; per_page?: number; send_status?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.send_status !== undefined) query.append('send_status', params.send_status.toString());
    const qs = query.toString();
    return get<EmailSendRecordListResponse>(`/email-tasks/${id}/records${qs ? '?' + qs : ''}`);
  },
};

// ==================== 邮件附件上传 ====================

export function uploadAttachment(
  file: File,
  onProgress: (percent: number) => void,
): Promise<{ data: { id: number; filename: string; file_size: number } }> {
  const fd = new FormData();
  fd.append('file', file);
  return uploadFile('/emails/attachments/upload', fd, onProgress);
}

export function uploadInlineImage(
  file: File,
  onProgress: (percent: number) => void,
): Promise<{ data: { id: number; cid: string; filename: string; file_size: number } }> {
  const fd = new FormData();
  fd.append('file', file);
  return uploadFile('/emails/inline-images/upload', fd, onProgress);
}
